import React from 'react';
import { Info, Sparkles } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
import { NeedsFlag } from '../ui/NeedsFlag';
type Group = 'ready' | 'conditional' | 'blocked';
type Suggestion = {
  text: string;
  tag: 'Health' | 'Botanical (IT)';
  rationale: string;
  note?: string;
  needs?: string;
};
const ITALY_NOTE = 'Italy-specific Allegato 1 reference wording — valid for the Italian market only.';
const VITAMIN_C_RATIONALE = 'Vitamin C clears the 15% NRV threshold; wording is the fixed Annex text under Regulation (EU) No 432/2012.';
const MANGANESE_RATIONALE = "Manganese clears the 15% NRV threshold. Conditional on the finished product consistently meeting the 'source of manganese' threshold.";
const MANGANESE_NEEDS = "confirm 'source of manganese' threshold is met";
const ready: Suggestion[] = [{
  text: 'La vitamina C contribuisce al normale metabolismo energetico',
  tag: 'Health',
  rationale: VITAMIN_C_RATIONALE
}, {
  text: 'La vitamina C contribuisce alla normale funzione del sistema immunitario',
  tag: 'Health',
  rationale: VITAMIN_C_RATIONALE
}, {
  text: 'La vitamina C contribuisce alla protezione delle cellule dallo stress ossidativo',
  tag: 'Health',
  rationale: VITAMIN_C_RATIONALE
}, {
  text: 'La tiamina contribuisce al normale metabolismo energetico',
  tag: 'Health',
  rationale: 'Thiamin clears the 15% NRV threshold; wording is the fixed Annex text under Regulation (EU) No 432/2012.'
}, {
  text: "[Estratto] contribuisce alla funzionalità del microcircolo (pesantezza delle gambe). Ha funzione antiossidante e supporta la regolare funzionalità dell'apparato cardiovascolare.",
  tag: 'Botanical (IT)',
  rationale: 'Grape seed extract (Vitis vinifera, semen) — matches the exact plant part used in this formula.',
  note: ITALY_NOTE
}];
const conditional: Suggestion[] = [{
  text: 'Il rame contribuisce alla normale funzione del sistema immunitario',
  tag: 'Health',
  rationale: "Copper clears the 15% NRV threshold, sitting exactly at Italy's minimum floor. Usable once batch testing confirms the real dose doesn't dip below it. Related copper angles: protection of cells from oxidative stress, normal iron transport.",
  needs: 'confirm exact Annex wording for the oxidative-stress and iron-transport angles'
}, {
  text: 'Il manganese contribuisce al normale metabolismo energetico',
  tag: 'Health',
  rationale: MANGANESE_RATIONALE,
  needs: MANGANESE_NEEDS
}, {
  text: 'Il manganese contribuisce al mantenimento di ossa normali',
  tag: 'Health',
  rationale: MANGANESE_RATIONALE,
  needs: MANGANESE_NEEDS
}, {
  text: 'Il manganese contribuisce alla normale formazione del tessuto connettivo',
  tag: 'Health',
  rationale: MANGANESE_RATIONALE,
  needs: MANGANESE_NEEDS
}, {
  text: 'Il manganese contribuisce alla protezione delle cellule dallo stress ossidativo',
  tag: 'Health',
  rationale: MANGANESE_RATIONALE,
  needs: MANGANESE_NEEDS
}, {
  text: '[Estratto] ha funzione antiossidante e di supporto al metabolismo dei carboidrati e dei lipidi. Contribuisce alla normale circolazione del sangue e alla regolarità della pressione arteriosa.',
  tag: 'Botanical (IT)',
  rationale: 'Olive extract — this formula combines leaf and fruit, while the reference wording is attributed to the leaf part alone.',
  note: ITALY_NOTE,
  needs: 'confirm wording applies to the combined leaf-and-fruit extract'
}];
const blocked: Suggestion[] = [{
  text: 'La vitamina E contribuisce alla protezione delle cellule dallo stress ossidativo',
  tag: 'Health',
  rationale: "Blocked — can't be used while the ingredient itself is over Italy's 60 mg/day ceiling. Resolve the vitamin E dose first (Formula tab, Blocker section)."
}];
export function ItalySuggestedClaimsTab() {
  return <div className="space-y-5" data-id="element-930" data-mp="2yukmxyof0g82">
      <header className="flex items-start gap-3" data-id="element-931" data-mp="33rb1uiohh5rw">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-932" data-mp="33q00sqmfac4h">
          <Sparkles className="h-5 w-5" data-id="element-933" data-mp="32c8o4jvc0ot9" />
        </span>
        <div data-id="element-934" data-mp="2xccfu8flqb7w">
          <h2 className="font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-935" data-mp="2xcvr56aqy1cy">Suggested claims</h2>
          <p className="mt-1 text-sm leading-relaxed text-[#6b7d70]" data-id="element-936" data-mp="3bcn91i4dw1w8">Claims this product may be eligible to make, based on its composition.</p>
        </div>
      </header>

      <p className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-937" data-mp="3l8hxitvagr6x">
        {ready.length} ready to use · {conditional.length} conditional · {blocked.length} blocked
      </p>

      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900" data-id="element-938" data-mp="3uzky7ukb345o">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" data-id="element-939" data-mp="3q0i3p41ycgby" />
        <span data-id="element-940" data-mp="7tlofu25z1ys">
          Every vitamin and mineral in this formula clears the 15% NRV significant-amount threshold, qualifying each for its corresponding authorised
          wording under Regulation (EU) No 432/2012 — including manganese, subject to the product meeting the &apos;source of manganese&apos; condition.
          For Italy, wording must appear in the official Italian-language text of the Regulation. Two botanical rows below use separate, Italy-specific
          Allegato 1 reference wording, valid only for the Italian market.
        </span>
      </div>

      <SuggestionGroup group="ready" label={`Ready to use · ${ready.length}`} items={ready} data-id="element-941" data-mp="3qksd6ktwpc7" />
      <SuggestionGroup group="conditional" label={`Conditional · ${conditional.length}`} items={conditional} data-id="element-942" data-mp="8hav0c1vee37" />
      <SuggestionGroup group="blocked" label={`Blocked · ${blocked.length}`} items={blocked} data-id="element-943" data-mp="3py2klnlhyn9p" />

      <div className="flex items-start gap-2 rounded-xl border border-[#dbe4d8] bg-[#f5f8f3] px-4 py-3 text-sm leading-relaxed text-[#607266]" data-id="element-944" data-mp="3uwm6xp40612l">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#718174]" data-id="element-945" data-mp="3gyfewplqivau" />
        Suggestions only — not a verification. Each claim must be confirmed and, where indicated, substantiated before use.
      </div>
    </div>;
}
function SuggestionGroup({
  group,
  label,
  items
}: {
  group: Group;
  label: string;
  items: Suggestion[];
}) {
  const dot = group === 'ready' ? 'bg-forest-green' : group === 'conditional' ? 'bg-amber-500' : 'bg-red-600';
  const heading = group === 'ready' ? 'text-forest-green' : group === 'conditional' ? 'text-amber-800' : 'text-red-700';
  return <section className="space-y-3" aria-label={label} data-id="element-946" data-mp="2afcyfzvm2vi3">
      <h3 className={`inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] ${heading}`} data-id="element-947" data-mp="2acqsu5sa6q2i">
        <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden="true" data-id="element-948" data-mp="21dwpzxocx3gi" />
        {label}
      </h3>
      {items.map(item => <article key={item.text} className={`rounded-2xl border bg-white p-5 shadow-sm ${group === 'blocked' ? 'border-red-200 border-l-4 border-l-red-600' : 'border-[#dce3da]'}`} data-id="element-949" data-mp="1we0n61rebi2j">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-950" data-mp="kybwp0exo0wz">
            <p className="font-[Figtree] text-base font-semibold leading-snug text-evergreen" data-id="element-951" data-mp="2wojo7mvt2fls">{item.text}</p>
            <span className="shrink-0 rounded-md bg-[#f0f3ef] px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#637467]" data-id="element-952" data-mp="31muy1xm3oq40">
              {item.tag}
            </span>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-[#56685c]" data-id="element-953" data-mp="3agrkzen3a6kh">{item.rationale}</p>
          {item.needs && <NeedsFlag className="mt-2" data-id="element-954" data-mp="3ffudy0psomc3">Needs: {item.needs}</NeedsFlag>}
          {item.note && <p className="mt-2 text-xs italic leading-relaxed text-[#8b968d]" data-id="element-955" data-mp="3kazz2xbdhwz4">{item.note}</p>}
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-956" data-mp="3pcfm2ux42dk5">
            {item.tag === 'Botanical (IT)' ? <RegulationLink text="DM 10 agosto 2018, Allegato 1" href="https://www.gazzettaufficiale.it/eli/id/2018/09/26/18A06095/SG" data-id="element-957" data-mp="3tczke1tf7n7v" /> : <>
                <RegulationLink text="Regulation (EU) No 432/2012" href="https://eur-lex.europa.eu/eli/reg/2012/432/oj/eng" data-id="element-958" data-mp="1z0jckwkz810w" />
                <RegulationLink text="REGOLAMENTO (UE) N. 432/2012" href="https://eur-lex.europa.eu/legal-content/IT/TXT/PDF/?uri=CELEX:32012R0432" data-id="element-959" data-mp="1g1ew4qcv9qem" />
              </>}
          </div>
        </article>)}
    </section>;
}